import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/src/components/Box";
import Loader from "@memorang/ui/src/components/Loader";
import { useImageDimensions } from "@react-native-community/hooks";
import { ImageBackground } from "expo-image";
import {
	createRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { CursorValue } from "react-native";
import { IconButton } from "react-native-paper";
import Animated, { useSharedValue } from "react-native-reanimated";
import Carousel, {
	type ICarouselInstance,
	Pagination,
} from "react-native-reanimated-carousel";
import type { AudioPlayerRef } from "../containers/AudioPlayerContainer";
import AudioPlayerContainer from "../containers/AudioPlayerContainer";

export type SlideData = {
	image: string;
	audio: string;
};

type SlideshowProps = {
	data: SlideData[];
	width?: number;
	onSlideChange?: (index: number) => void;
	autoAdvance?: boolean;
	initialIndex?: number;
	loading?: boolean;
	itemId: number;
	onDurationLoaded?: (durationInSeconds: number) => void;
};

export const Slideshow = ({
	data,
	width = 420,
	onSlideChange,
	autoAdvance = true,
	initialIndex = 0,
	loading,
	itemId,
	onDurationLoaded,
}: SlideshowProps) => {
	const ref = useRef<ICarouselInstance>(null);
	const audioPlayerRef = useRef<React.RefObject<AudioPlayerRef>[]>([]);
	const [isUserInteraction, setIsUserInteraction] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const [isHovered, setIsHovered] = useState(false);
	const progress = useSharedValue<number>(0);
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const [waveformWidth, setWaveformWidth] = useState(0);
	const [showWaveform, setShowWaveform] = useState(true);
	const [durationsInMilliseconds, setDurationsInMilliseconds] = useState<
		number[]
	>([]);

	const handleOnDurationLoaded = useCallback(
		(index: number, durationInMilliseconds: number) => {
			setDurationsInMilliseconds((prev) => {
				const newDurations = [...prev];
				newDurations[index] = durationInMilliseconds;
				return newDurations;
			});
		},
		[],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: called only when values change
	useEffect(() => {
		if (
			onDurationLoaded &&
			durationsInMilliseconds.filter(Boolean).length === data.length
		) {
			onDurationLoaded(
				durationsInMilliseconds.reduce((a, b) => a + b, 0) / 1000, // Convert to seconds
			);
		}
	}, [durationsInMilliseconds, data.length]);

	audioPlayerRef.current = data.map((_, index) => {
		return audioPlayerRef.current[index] || createRef<AudioPlayerRef>();
	});

	// This to ensure that the audios are preloaded
	const AudioPlayers = useMemo(() => {
		return data
			.filter((item) => item.audio)
			.map((item, index) => {
				return (
					<AudioPlayerContainer
						useReanimated={false}
						itemId={itemId}
						key={item.audio}
						ref={audioPlayerRef.current[index]}
						uri={item.audio}
						hidden={index !== currentIndex}
						onEnd={() => {
							if (!isUserInteraction && autoAdvance) {
								ref.current?.next();
								audioPlayerRef.current[index + 1]?.current?.play();
							}
						}}
						onReplay={() => {
							if (isUserInteraction) {
								setIsUserInteraction(false);
								for (
									let i = currentIndex;
									i < audioPlayerRef.current.length;
									i++
								) {
									audioPlayerRef.current[i]?.current?.seekTo(0);
								}
							}
						}}
						waveformWidth={waveformWidth}
						updateWaveformWidth={setWaveformWidth}
						showWaveform={showWaveform}
						onDurationLoaded={(duration) =>
							handleOnDurationLoaded(index, duration)
						}
						// autoPlay={index === 0}
					/>
				);
			});
	}, [
		data,
		currentIndex,
		isUserInteraction,
		autoAdvance,
		waveformWidth,
		showWaveform,
		handleOnDurationLoaded,
		itemId,
	]);

	const { loading: dimensionsLoading, dimensions } = useImageDimensions({
		uri: data[0].image,
	});

	const height = width / (dimensions?.aspectRatio || 4 / 3);
	const loaderHeight = height + 90; // carousel + audio player

	const showLeftButton = isHovered && currentIndex > 0;
	const showRightButton = isHovered && currentIndex < data.length - 1;

	const handlePrevious = () => {
		ref.current?.prev();
		setIsUserInteraction(true);
	};

	const handleNext = () => {
		ref.current?.next();
		setIsUserInteraction(false);
	};

	if (dimensionsLoading || loading) {
		return (
			<Box height={loaderHeight} width={width}>
				<Loader />
			</Box>
		);
	}

	const renderItem = ({ item }: { item: SlideData }) => (
		<ImageBackground
			key={item.image}
			source={{ uri: item.image }}
			style={{
				flex: 1,
				width: "100%",
				height: "100%",
				justifyContent: "center",
				alignItems: isMobile ? "center" : "flex-start",
			}}
			blurRadius={20}
		>
			<Animated.Image
				source={{ uri: item.image }}
				style={{
					height: "100%",
					width: "100%",
					resizeMode: "contain",
					cursor: "grab" as unknown as CursorValue,
				}}
			/>
		</ImageBackground>
	);

	const playAudio = async (index: number) => {
		for (let i = 0; i < audioPlayerRef.current.length; i++) {
			if (i === index) {
				await audioPlayerRef.current[i]?.current?.seekTo(0);
				audioPlayerRef.current[i]?.current?.play();
			} else {
				audioPlayerRef.current[i]?.current?.pause();
			}
		}
	};

	const onPressPagination = (index: number) => {
		ref.current?.scrollTo({
			/**
			 * Calculate the difference between the current index and the target index
			 * to ensure that the carousel scrolls to the nearest index
			 */
			count: index - progress.value,
			animated: true,
		});
	};

	return (
		<Box flex={1} height="100%" gap={16}>
			<Box
				height={height + 12}
				position="relative"
				gap={16}
				width={width}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<Carousel
					ref={ref}
					autoPlay={false}
					data={data}
					height={height}
					loop={false}
					pagingEnabled={true}
					snapEnabled={true}
					width={width}
					style={{
						alignSelf: isMobile ? "center" : "flex-start",
					}}
					modeConfig={{
						snapDirection: "left",
						stackInterval: 18,
					}}
					onProgressChange={(_progress, absoluteProgress) => {
						progress.value = absoluteProgress;

						const roundedAbsoluteProgress = Math.round(absoluteProgress);

						if (roundedAbsoluteProgress < currentIndex) {
							setIsUserInteraction(true);
						}
						setCurrentIndex(roundedAbsoluteProgress);
						onSlideChange?.(roundedAbsoluteProgress);
						if (
							roundedAbsoluteProgress !== currentIndex &&
							!audioPlayerRef.current[roundedAbsoluteProgress]?.current
								?.isPlaying
						) {
							playAudio(roundedAbsoluteProgress);
						}
					}}
					onScrollStart={() => {
						if (!isWeb) {
							setShowWaveform(false);
						}
					}}
					onScrollEnd={() => {
						if (!isWeb) {
							setShowWaveform(true);
						}
					}}
					customConfig={() => ({ type: "positive", viewCount: 4 })}
					renderItem={renderItem}
				/>
				{showLeftButton && (
					<Box
						position="absolute"
						left={8}
						top="50%"
						style={{ transform: [{ translateY: -20 }] }}
					>
						<IconButton
							icon="arrow-left"
							mode="contained-tonal"
							size={24}
							onPress={handlePrevious}
						/>
					</Box>
				)}
				{showRightButton && (
					<Box
						position="absolute"
						right={8}
						top="50%"
						style={{ transform: [{ translateY: -20 }] }}
					>
						<IconButton
							icon="arrow-right"
							mode="contained-tonal"
							size={24}
							onPress={handleNext}
						/>
					</Box>
				)}
				<Pagination.Basic
					onPress={onPressPagination}
					progress={progress}
					data={data}
					dotStyle={{
						width: 25,
						height: 4,
						backgroundColor: theme.colors.secondaryContainer,
						cursor: "pointer",
					}}
					activeDotStyle={{
						overflow: "hidden",
						backgroundColor: theme.colors.primary,
					}}
					containerStyle={{
						gap: 10,
						position: "absolute",
						bottom: 0,
					}}
					horizontal
				/>
			</Box>
			<Box width={width} alignSelf={isMobile ? "center" : "flex-start"}>
				{AudioPlayers}
			</Box>
		</Box>
	);
};
